<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div class="space-y-2">
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
          <BaseInput
            v-model="modifiedData.name"
            color="base"
            show-error-as-tooltip
            :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
        </div>
        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
          <BaseInput
            v-model="modifiedData.code"
            color="base"
            show-error-as-tooltip
            :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
        </div>

        <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <label for="code" class="text-sm font-medium">{{ $t('baseObject.boid') }}</label>
          <BaseInput v-model="modifiedData.boid" color="base" />
        </div>

        <BaseToggle
          :label="$t('baseObject.isActive')"
          name="isActive"
          v-model="modifiedData.isActive"
          light
          :disable="!modifiedData.isFromManager" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditVatCategory } from '@/services/api/vat';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import type { VatCategory } from '@/types/Vat';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    vatCategory: VatCategory;
  }>(),
  {}
);

const modifiedData = ref<VatCategory>(deepCopy<VatCategory>(props.vatCategory));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.vatCategory);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

onMounted(async () => {});

function onDismissChanges() {
  modifiedData.value = deepCopy<VatCategory>(props.vatCategory);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditVatCategory(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
